import { vec2 } from "@tlaukkan/tsm";
import BezierEasing from "bezier-easing";
import { Entity, System } from "ecsy";
import { calculateTargetLead } from "../box2dUtils";
import { Cooldown, Ownership, Physics, Shoot, ShotPrice, SpendMoney, Sync, Transform, VisibleRange } from "../components";
import { getEntityPlayer } from "../composition";
import { CellCategory } from "../constants";
import { createEntity } from "../entityFactory";
import { Game } from "../game";
import { GameElement } from "../gameElementTypes";
import { selectAndFilter, SimpleSelector, TargetFilter, TargetFilterType, TargetType } from "../triggeredEffects";
import { angleFromUp } from "../utils";

export class ShootSystem extends System {
	private game: Game;
	private easing: BezierEasing.EasingFunction;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.easing = BezierEasing(0, 1, 1, 0);
	}

	execute() {
		if (!this.game.inSimulation) return;

		this.queries.shooters.results.forEach(entity => {
			const shoot = entity.getMutableComponent(Shoot);
			const transform = entity.getComponent(Transform);
			const sync = entity.getComponent(Sync);
			const owned = entity.getComponent(Ownership);
			const playerId = owned ? owned.playerId : -1;

			let target: Entity | null = null;
			const range = entity.getComponent(VisibleRange);
			if (range && range.nearest) {
				target = range.nearest;
			}

			//Aim if applicable
			let targetPosition: vec2;
			if (shoot.aim) {
				if (target === null) return;
				if (!target.hasComponent(Transform)) return;
				const targetTransform = target.getComponent(Transform);
				transform.direction = vec2.direction(targetTransform.position, transform.position);
				//targetPosition = targetTransform.position.copy();
				targetPosition = calculateTargetLead(target, transform.position, undefined, targetTransform);
			}
			else {
				targetPosition = transform.position.copy().add(transform.forward.copy());
			}

			//Wait for cooldown if applicable
			const cooldown = entity.getComponent(Cooldown);
			if (cooldown) {
				if (!cooldown.done) {
					return;
				}
			}

			//Abort if can't pay price
			const price = entity.getComponent(ShotPrice);
			if (price) {
				const player = getEntityPlayer(entity, this.game);
				if (player && player.currentFunds >= price.amount) {
					entity.addComponent(SpendMoney, { amount: price.amount });
				}
				else {
					return;
				}
			}

			//Restart cooldown, this must come after spending price, otherwise cooldown would loop with no shot
			if (cooldown && !shoot.fireOnSight) cooldown.restart();

			//Wait until target sighted if applicable
			if (shoot.fireOnSight) {
				if (target) {
					cooldown.restart();
				}
				else {
					return;
				}
			}

			if (shoot.shotsFired > shoot.shots) {
				return;
			}

			// Adjust size if necessary
			let size = 0.5;
			let hp = 1;
			if (shoot.shotHealth > 1) {
				hp = shoot.shotHealth;
			}
			if (shoot.scale) {
				const selector: SimpleSelector = { type: TargetType.Team }
				const filters: TargetFilter[] = [
					{ type: TargetFilterType.Category, category: CellCategory.BCell },
					{ type: TargetFilterType.ImmuneCell },
				];
				const targets = selectAndFilter(selector, filters, entity, this.game);
				size = Math.min(0.25 * targets.length, 2.5);
				hp = targets.length;
			} 

			const lineToTarget = vec2.difference(targetPosition, transform.position);
			const direction = lineToTarget.copy().normalize();
			const angle = angleFromUp(direction);

			//Shoot
			let initialPush: [number, number] | undefined = undefined;
			if (shoot.initialImpulse > 0) {
				initialPush = lineToTarget.scale(shoot.initialImpulse).xy
			}

			++shoot.shotsFired;

			createEntity({
				type: shoot.ammo,
				position: transform.position.xy,
				rotation: angle,
				playerId,
				unsynced: !sync,
				entityParameters: {
					creatorSyncId: sync ? sync.id : undefined,
					targetPosition: targetPosition.xy, //not used, but left in in case
					targetDirection: direction.xy,
					hp,
					size,
					initialPush
				}
			});
		});
	}
}

ShootSystem.queries = {
	shooters: { components: [Shoot, Physics] },
}